import React from "react";

// Card Props
export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: "default" | "elevated" | "outlined" | "glass";
  padding?: "none" | "sm" | "md" | "lg";
  hover?: boolean;
  clickable?: boolean;
}

// Card Header Props
export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  divider?: boolean;
}

// Card Body Props
export interface CardBodyProps extends React.HTMLAttributes<HTMLDivElement> {
  scrollable?: boolean;
  maxHeight?: string;
}

// Card Footer Props
export interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  divider?: boolean;
}

/**
 * Arc-style Card component
 * 
 * Features:
 * - Multiple variants: default, elevated, outlined, glass
 * - Configurable padding
 * - Hover effects
 * - Clickable state
 * - Compound components: CardHeader, CardBody, CardFooter
 */
const Card: React.FC<CardProps> & {
  Header: React.FC<CardHeaderProps>;
  Body: React.FC<CardBodyProps>;
  Footer: React.FC<CardFooterProps>;
} = ({
  variant = "default",
  padding = "none",
  hover = false,
  clickable = false,
  children,
  className = "",
  ...props
}) => {
  // Variant styles
  const variantStyles = {
    default: `
      bg-surface-elevated
      border border-arc-border
      shadow-arc-sm
    `,
    elevated: `
      bg-surface-elevated
      shadow-arc-lg
    `,
    outlined: `
      bg-surface-elevated
      border border-arc-border
    `,
    glass: `
      bg-surface-elevated/80
      backdrop-blur-arc-md
      border border-arc-border-subtle
      shadow-arc-glass
    `,
  };

  // Padding styles
  const paddingStyles = {
    none: "",
    sm: "p-3",
    md: "p-4",
    lg: "p-6",
  };

  // Hover styles
  const hoverStyles = hover
    ? "transition-all duration-arc-normal hover:shadow-arc-lg hover:-translate-y-0.5"
    : "";

  // Clickable styles
  const clickableStyles = clickable
    ? "cursor-pointer active:scale-[0.99] transition-transform duration-arc-fast"
    : "";

  return (
    <div
      className={`
        rounded-arc-lg
        overflow-hidden
        ${variantStyles[variant]}
        ${paddingStyles[padding]}
        ${hoverStyles}
        ${clickableStyles}
        ${className}
      `}
      {...props}
    >
      {children}
    </div>
  );
};

/**
 * Card Header component
 */
const CardHeader: React.FC<CardHeaderProps> = ({
  divider = false,
  children,
  className = "",
  ...props
}) => {
  return (
    <div
      className={`
        px-6 py-4
        ${divider ? "border-b border-arc-border" : ""}
        ${className}
      `}
      {...props}
    >
      {children}
    </div>
  );
};

/**
 * Card Body component
 */
const CardBody: React.FC<CardBodyProps> = ({
  scrollable = false,
  maxHeight,
  children,
  className = "",
  style,
  ...props
}) => {
  return (
    <div
      className={`
        px-6 py-4
        ${scrollable ? "overflow-y-auto" : ""}
        ${className}
      `}
      style={{
        ...(maxHeight && scrollable ? { maxHeight } : {}),
        ...style,
      }}
      {...props}
    >
      {children}
    </div>
  );
};

/**
 * Card Footer component
 */
const CardFooter: React.FC<CardFooterProps> = ({
  divider = false,
  children,
  className = "",
  ...props
}) => {
  return (
    <div
      className={`
        px-6 py-4
        ${divider ? "border-t border-arc-border" : ""}
        ${className}
      `}
      {...props}
    >
      {children}
    </div>
  );
};

// Display names for debugging
Card.displayName = "Card";
CardHeader.displayName = "CardHeader";
CardBody.displayName = "CardBody";
CardFooter.displayName = "CardFooter";

// Attach compound components
Card.Header = CardHeader;
Card.Body = CardBody;
Card.Footer = CardFooter;

// Named exports for backwards compatibility
export { Card, CardHeader, CardBody, CardFooter };
